import React, { useRef, useState } from "react";

export function extractYouTubeId(url) {
  if (!url) return null;
  // youtu.be/ID
  const short = url.match(/youtu\.be\/([^?&#/]+)/);
  if (short) return short[1];
  // youtube.com/shorts/ID
  const shorts = url.match(/shorts\/([^?&#/]+)/);
  if (shorts) return shorts[1];
  // youtube.com/watch?v=ID  or  youtube.com/embed/ID
  const standard = url.match(/(?:[?&]v=|embed\/)([^?&#/]+)/);
  if (standard) return standard[1];
  return null;
}

export function isYouTubeUrl(url) {
  return typeof url === "string" && /youtube\.com|youtu\.be/.test(url);
}

function YouTubeBackground({ url }) {
  const iframeRef = useRef(null);
  const [muted, setMuted] = useState(true); // start muted so autoplay is guaranteed

  const videoId = extractYouTubeId(url);
  if (!videoId) return null;

  const src =
    `https://www.youtube-nocookie.com/embed/${videoId}` +
    `?autoplay=1&mute=1&loop=1&playlist=${videoId}` +
    `&controls=0&rel=0&enablejsapi=1&playsinline=1&showinfo=0&autoplay=1`;

  const toggleMute = () => {
    const next = !muted;
    setMuted(next);
    iframeRef.current?.contentWindow?.postMessage(
      JSON.stringify({
        event: "command",
        func: next ? "mute" : "unMute",
        args: [],
      }),
      "*",
    );
  };

  return (
    <>
      {/* ── Full-screen video layer ── */}
      <div
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 0,
          overflow: "hidden",
          pointerEvents: "none",
          backgroundColor: "#000",
        }}
      >
        <iframe
          frameBorder="0"
          ref={iframeRef}
          src={src}
          title="background video"
          allow="autoplay; encrypted-media; picture-in-picture"
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            /* Cover viewport regardless of aspect ratio */
            width: "max(100vw, calc(100vh * 16 / 9))",
            height: "max(100vh, calc(100vw * 9 / 16))",
            transform: "translate(-50%, -50%)",
            border: "none",
            pointerEvents: "none",
          }}
        />
      </div>

      {/* ── Mute / unmute button ── */}
      <button
        onClick={toggleMute}
        title={muted ? "Unmute background" : "Mute background"}
        style={{
          position: "fixed",
          bottom: "28px",
          right: "80px", // leave space away from buy-coffee btn
          zIndex: 20,
          background: "rgba(0,0,0,0.55)",
          border: "1px solid rgba(255,255,255,0.18)",
          color: "#f5e8c7cc",
          borderRadius: "9999px",
          padding: "7px 16px",
          fontSize: "12px",
          fontFamily: "'Montserrat', sans-serif",
          fontWeight: "bold",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          gap: "7px",
          backdropFilter: "blur(8px)",
          letterSpacing: "0.04em",
        }}
      >
        {muted ? (
          // muted icon
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
            <line x1="23" y1="9" x2="17" y2="15" />
            <line x1="17" y1="9" x2="23" y2="15" />
          </svg>
        ) : (
          // sound icon
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
            <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
            <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
          </svg>
        )}
        {muted ? "Unmute" : "Mute"}
      </button>
    </>
  );
}

export default YouTubeBackground;
